import { BLEEDING_OPTIONS, OVULATION_TEST_OPTIONS } from "../cycleLogic";
import { formatTemperature } from "../temperature";
import { escapeSpreadsheetText, serializeCsv, UTF8_BOM } from "./csv";

/**
 * CSV export of observations. The column set and header names here are the contract the
 * importer (observationsCsvImport.ts) auto-detects, so an exported file always round-trips:
 * rename a header only by keeping the old name as an import alias.
 */

export type ObservationCsvField =
  | "observation_date"
  | "cycle_day"
  | "is_cycle_start"
  | "temperature_f"
  | "bleeding"
  | "mucus_consistency"
  | "mucus_color"
  | "mucus_frequency"
  | "mucus"
  | "monitor"
  | "ovulation_test"
  | "intercourse"
  | "symptoms"
  | "notes";

/** Export column order and header names. */
export const OBSERVATION_CSV_COLUMNS: readonly { field: ObservationCsvField; header: string }[] = [
  { field: "observation_date", header: "Date" },
  { field: "cycle_day", header: "Cycle Day" },
  { field: "is_cycle_start", header: "Cycle Start" },
  { field: "temperature_f", header: "Temperature (°F)" },
  { field: "bleeding", header: "Bleeding" },
  { field: "mucus_consistency", header: "Mucus Consistency" },
  { field: "mucus_color", header: "Mucus Color" },
  { field: "mucus_frequency", header: "Mucus Frequency" },
  { field: "mucus", header: "Mucus Summary" },
  { field: "monitor", header: "Monitor" },
  { field: "ovulation_test", header: "Ovulation Test" },
  { field: "intercourse", header: "Intercourse" },
  { field: "symptoms", header: "Symptoms" },
  { field: "notes", header: "Notes" },
];

/** Separator for the multi-valued Symptoms cell. */
export const SYMPTOMS_CSV_SEPARATOR = "; ";

/** The observation shape the exporter reads (a DB row plus an optional derived cycle day). */
export interface ObservationCsvRecord {
  observation_date: string;
  cycle_day?: number | null;
  is_cycle_start?: boolean | null;
  temperature_f?: number | string | null;
  bleeding?: number | null;
  mucus_consistency?: string | null;
  mucus_color?: string | null;
  mucus_frequency?: string | null;
  mucus?: string | null;
  monitor?: string | null;
  ovulation_test?: string | null;
  intercourse?: boolean | null;
  symptoms?: string[] | null;
  notes?: string | null;
}

const yesNo = (value: boolean | null | undefined): string =>
  value === true ? "Yes" : value === false ? "No" : "";

const text = (value: string | null | undefined): string =>
  value ? escapeSpreadsheetText(value) : "";

export const formatBleedingForCsv = (value: number | null | undefined): string => {
  if (value == null) return "";
  return BLEEDING_OPTIONS.find((o) => o.value === value)?.label ?? String(value);
};

const formatTemperatureForCsv = (value: number | string | null | undefined): string => {
  if (value == null || value === "") return "";
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? formatTemperature(parsed) : "";
};

const formatOvulationTestForCsv = (value: string | null | undefined): string =>
  value ? OVULATION_TEST_OPTIONS.find((o) => o.value === value)?.label ?? value : "";

const cellFor = (record: ObservationCsvRecord, field: ObservationCsvField): string => {
  switch (field) {
    case "observation_date":
      return record.observation_date;
    case "cycle_day":
      return record.cycle_day != null ? String(record.cycle_day) : "";
    case "is_cycle_start":
      return record.is_cycle_start ? "Yes" : "No";
    case "temperature_f":
      return formatTemperatureForCsv(record.temperature_f);
    case "bleeding":
      return formatBleedingForCsv(record.bleeding);
    case "ovulation_test":
      return formatOvulationTestForCsv(record.ovulation_test);
    case "intercourse":
      return yesNo(record.intercourse);
    case "symptoms":
      return record.symptoms?.length
        ? escapeSpreadsheetText(record.symptoms.join(SYMPTOMS_CSV_SEPARATOR))
        : "";
    case "mucus_consistency":
    case "mucus_color":
    case "mucus_frequency":
    case "mucus":
    case "monitor":
    case "notes":
      return text(record[field]);
  }
};

export interface BuildObservationsCsvOptions {
  /**
   * Prefix a UTF-8 byte-order mark (default true). Excel on Windows otherwise decodes a CSV as
   * the legacy ANSI code page, garbling the "°F" header and any non-ASCII notes; Google Sheets,
   * Numbers and our own importer all ignore the BOM.
   */
  bom?: boolean;
}

/** Build the full CSV document (header + one row per observation, in the order given). */
export const buildObservationsCsv = (
  records: readonly ObservationCsvRecord[],
  options: BuildObservationsCsvOptions = {}
): string => {
  const header = OBSERVATION_CSV_COLUMNS.map((c) => c.header);
  const rows = records.map((record) => OBSERVATION_CSV_COLUMNS.map((c) => cellFor(record, c.field)));
  const body = serializeCsv([header, ...rows]);
  return (options.bom ?? true) ? `${UTF8_BOM}${body}\r\n` : `${body}\r\n`;
};
